import { useEffect, useState } from "react";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { ACTIVE_SUPPLIER_CONNECTOR_KINDS } from "@mavatech/shared";
import { Button, Dialog, Input, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

const KIND_LABEL: Record<string, string> = {
  datanorm: "nur Katalog (Datanorm-Datei)",
  ids_connect: "IDS Connect (Absprung in den Händlershop)",
};
const CRED_LABEL: Record<string, string> = {
  customerNumber: "Kundennummer",
  username: "Benutzer (Impex: Kartennummer des Webshops)",
  password: "Passwort",
};
/** Vorbelegung der IDS-Felder je Händler (nach Namen) — Zugangsdaten gibt es hier bewusst nicht. */
function idsDefaults(name: string | undefined): Record<string, string> {
  const n = (name ?? "").toLowerCase();
  const base = { idsVersion: "2.0" };
  if (n.includes("impex"))
    return { ...base, punchoutUrl: "https://shop.impex.at/IDS.do" };
  if (/steiner|gc[ -]?gruppe|\bgc\b/.test(n))
    return { ...base, punchoutUrl: "https://www.gconlineplus.at/ids.aspx" };
  return base;
}
const CFG_PLACEHOLDER: Record<string, string> = {
  idsVersion: "2.0",
  idsAction: "WKE (Standard, leer lassen)",
};
const CFG_LABEL: Record<string, string> = {
  punchoutUrl: "IDS-Adresse des Händlers",
  idsVersion: "IDS-Connect-Version (z. B. 2.0)",
  idsAction: "IDS-Aktion (leer = WKE, Warenkorb empfangen)",
};

export function ConnectorDialog({
  supplierId,
  open,
  onClose,
}: {
  supplierId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const status = trpc.connector.status.useQuery(
    { supplierId: supplierId! },
    { enabled: open && !!supplierId },
  );

  const [kind, setKind] = useState("none");
  const [cfg, setCfg] = useState<Record<string, string>>({});
  const [cred, setCred] = useState<Record<string, string>>({});
  const spec = trpc.connector.spec.useQuery(
    { kind: kind as never },
    { enabled: open },
  );

  useEffect(() => {
    if (!open || !status.data) return;
    // Altwerte (kein Konnektor, OCI, UGL, Open Masterdata) werden nicht mehr unterstützt → als Katalog-Lieferant zeigen
    setKind(
      (ACTIVE_SUPPLIER_CONNECTOR_KINDS as readonly string[]).includes(
        status.data.connectorKind,
      )
        ? status.data.connectorKind
        : "datanorm",
    );
    const loaded = { ...(status.data.config as Record<string, string>) };
    if (status.data.connectorKind === "ids_connect")
      for (const [k, v] of Object.entries(idsDefaults(status.data.supplierName)))
        if (!loaded[k]) loaded[k] = v;
    setCfg(loaded);
    setCred({});
  }, [open, status.data]);

  const save = trpc.connector.setConfig.useMutation({
    onSuccess: () => {
      utils.connector.status.invalidate({ supplierId: supplierId! });
      utils.supplier.list.invalidate();
      addToast({ type: "success", title: "Konnektor gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const test = trpc.connector.test.useMutation({
    onSuccess: (r) =>
      addToast({
        type: r.ok ? "success" : "warning",
        title: r.ok ? "Konfiguration ok" : "Noch nicht bereit",
        description: r.message,
      }),
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const punchout = trpc.connector.startPunchout.useMutation({
    onSuccess: (r) => {
      if (r.post) {
        // IDS Connect verlangt POST: verstecktes Formular in neuem Tab abschicken. Die Zugangsdaten gehen
        // so direkt an den Händler und stehen nicht in der Adresszeile. Bewusst Standard-Kodierung
        // (urlencoded): Impex wertet multipart nur in der Anmeldestufe aus, danach fehlt der Aktionscode.
        const form = document.createElement("form");
        form.method = "POST";
        form.action = r.post.url;
        form.target = "_blank";
        form.style.display = "none";
        for (const [k, v] of Object.entries(r.post.fields)) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v;
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
        form.remove();
      } else {
        window.open(r.redirectUrl, "_blank", "noopener");
      }
      addToast({
        type: "info",
        title: "Shop geöffnet",
        description: "Der Warenkorb wird nach Rückkehr übernommen.",
      });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });

  const s = spec.data;
  // Die Kundennummer ist bei IDS Connect nicht bei jedem Händler nötig (Impex: nicht, Auskunft per
  // action=LI) — als optionales Feld anzeigen, falls ein Händler sie doch verlangt.
  const credFields = [
    ...(s?.requiredCredentialFields ?? []),
    ...(kind === "ids_connect" ? (["customerNumber"] as const) : []),
  ];
  // idsVersion ist optional (Code-Default "2.0"), aber bei IDS Connect trotzdem anzeigen —
  // manche Händler unterstützen nur ältere Versionen (z. B. 1.3), das muss einstellbar sein.
  const cfgFields = [
    ...(s?.requiredConfigFields ?? []),
    ...(kind === "ids_connect" ? (["idsVersion", "idsAction"] as const) : []),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Großhändler-Konnektor"
      wide
    >
      <div className="space-y-4">
        <Select
          label="Konnektor-Typ"
          value={kind}
          onChange={(e) => {
            const k = e.target.value;
            setKind(k);
            // IDS Connect gewählt: Adresse + Version vorbelegen (nur leere Felder, nichts überschreiben)
            if (k === "ids_connect")
              setCfg((c) => {
                const d = idsDefaults(status.data?.supplierName);
                return {
                  ...c,
                  ...Object.fromEntries(
                    Object.entries(d).filter(([key]) => !c[key]),
                  ),
                };
              });
          }}
          options={ACTIVE_SUPPLIER_CONNECTOR_KINDS.map((k) => ({
            value: k,
            label: KIND_LABEL[k] ?? k,
          }))}
        />

        {!!status.data?.priceCount && (
          <p className="text-xs text-text-secondary">
            Katalog (Datanorm): {status.data.priceCount.toLocaleString("de-AT")}{" "}
            Preise · bleibt unverändert, auch wenn du unten einen Konnektor
            (z. B. IDS Connect) einrichtest.
          </p>
        )}

        {status.data?.keyWarning && (
          <p className="text-xs text-warning flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> {status.data.keyWarning}
          </p>
        )}

        {cfgFields.length > 0 && (
          <div>
            <p className="label">Einstellungen</p>
            <div className="grid grid-cols-2 gap-3">
              {cfgFields.map((f) => (
                <Input
                  key={f}
                  label={CFG_LABEL[f] ?? f}
                  placeholder={CFG_PLACEHOLDER[f]}
                  value={cfg[f] ?? ""}
                  onChange={(e) =>
                    setCfg((c) => ({ ...c, [f]: e.target.value }))
                  }
                />
              ))}
            </div>
          </div>
        )}

        {kind === "ids_connect" && (
          <p className="text-2xs text-text-tertiary -mt-2">
            Impex: https://shop.impex.at/IDS.do (Benutzer = Kartennummer) ·
            GC Online Plus (Steiner u. a.): https://www.gconlineplus.at/ids.aspx
            (Benutzer + Kundennummer + Passwort). Version 2.0, Aktion leer
            lassen.
          </p>
        )}

        {credFields.length > 0 && (
          <div>
            <p className="label">Zugangsdaten (verschlüsselt gespeichert)</p>
            <div className="grid grid-cols-2 gap-3">
              {credFields.map((f) => (
                <Input
                  key={f}
                  label={
                    kind === "ids_connect" && f === "customerNumber"
                      ? "Kundennummer (GC Online Plus/Steiner: nötig · Impex: leer lassen)"
                      : (CRED_LABEL[f] ?? f)
                  }
                  type={f === "password" ? "password" : "text"}
                  placeholder={
                    status.data?.credentialsSet?.[f] ? "•••••• (gesetzt)" : ""
                  }
                  value={cred[f] ?? ""}
                  onChange={(e) =>
                    setCred((c) => ({ ...c, [f]: e.target.value }))
                  }
                />
              ))}
            </div>
            <p className="text-2xs text-text-tertiary mt-1">
              Leer lassen = unverändert. Zum Löschen ein Leerzeichen eingeben
              und speichern.
            </p>
          </div>
        )}

        {s && (
          <p className="text-xs text-text-tertiary">
            Unterstützt:{" "}
            {Object.entries(s.supports)
              .filter(([, v]) => v)
              .map(([k]) => k)
              .join(", ") || "—"}
            {status.data &&
              (status.data.ready
                ? " · bereit"
                : status.data.missing.length
                  ? ` · fehlt: ${status.data.missing.join(", ")}`
                  : "")}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {kind === "ids_connect" && (
            <Button
              variant="ghost"
              disabled={!status.data?.ready}
              onClick={() => punchout.mutate({ supplierId: supplierId! })}
              loading={punchout.isPending}
            >
              <ExternalLink className="w-4 h-4" /> Shop öffnen
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => test.mutate({ supplierId: supplierId! })}
            loading={test.isPending}
          >
            Verbindung testen
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                supplierId: supplierId!,
                connectorKind: kind as never,
                config: cfg,
                credentials: cred,
              })
            }
          >
            Speichern
          </Button>
        </div>

        <p className="text-2xs text-text-tertiary border-t border-border-subtle pt-3">
          Unterstützt werden der Katalog per Datanorm-Datei und IDS Connect
          (Absprung in den Shop des Händlers, Warenkorb zurück ins Angebot).
          IDS braucht je Händler die Freischaltung und Zugangsdaten.
        </p>
      </div>
    </Dialog>
  );
}
